import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import queryString from "query-string";
import { Loading } from "@alm/flow-ds/components";
import { useHistory } from "react-router-dom";
import { ListGroup, ListGroupItem } from "../ListGroup";
import MyTaskCard from "./MyTaskCard";
import { useTaskList } from "../../hooks/useTaskList";
import ReassignDialog from "components/ReassignDialog";
import api from "lib/api";
import usePagedData from "hooks/usePagedData";
import TasksHeading from "./TasksHeading";
import CustomLink from "components/CustomLink";
import useCheckMountedStatus from "hooks/useCheckMountedStatus";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import DeleteDialog from "components/DeleteDialog";
import { useMobileView } from "hooks/useMobileView";
import PaginationBar from "components/PaginationBar";

interface Props {
	search: string;
}

function MyTasks({ search }: Props) {
	const { t } = useTranslation();

	const history = useHistory();

	const { _isMounted } = useCheckMountedStatus();

	const [init, setInit] = useState(false);

	const [currentPage, setCurrentPage] = useState(1);

	const [total, setTotal] = useState<number | undefined>(0);

	const { paginationLimit } = useMobileView();

	// Hook to page tasks
	const { state, setData, ready, patchState, loading, searching } =
		usePagedData();

	const { limit } = state;

	const disableOnLoad = state.loading ? "disabled" : "";

	// Hook to handle task data changes
	const { handleReadUnread, changePriority, unassignTask, deleteActivity } =
		useTaskList(state, patchState);

	// Reassign query param value
	const { reassign }: { reassign?: string } = queryString.parse(
		window.location.search
	);

	// DeleteId query param value
	const { deleteId = "" }: { deleteId?: string } = queryString.parse(
		window.location.search
	);

	/**
	 * Get menu and menu list items using id
	 *
	 * @id of task
	 */
	const getMenuSelectors = useCallback(id => {
		return {
			menu: document.querySelector(`#taskMenu__dropdown__${id}`),
			menuList: document.querySelector(
				`#taskMenu__dropdown__${id} + div`
			),
		};
	}, []);

	/**
	 * Checks for clicks on the task item to see if it's not the
	 * task menu dropdown
	 *
	 * @param e event object
	 * @param id of task
	 */
	const handleOnClick = useCallback(
		(e, id) => {
			e.preventDefault();
			// Get menu selectors so we know which elements to ignore
			const selectors = getMenuSelectors(id);

			if (
				!selectors.menu?.contains(e.target) &&
				!selectors.menuList?.contains(e.target)
			) {
				addSentryBreadcrumb({
					category: "click",
					message: `Task ${id}`,
				});
				history.push(`/task/${id}`);
			}
		},
		[getMenuSelectors, history]
	);

	/**
	 * Checks if key is spacebar or enter
	 *
	 * @param e event object
	 * @param id of task
	 */
	const handleKeyDown = useCallback(
		(e, id) => {
			// Get menu selectors so we know which elements to ignore
			const selectors = getMenuSelectors(id);

			// If key is spacebar or enter and target isn't task dropdown then go to task
			if (
				(e.key === "Enter" || e.keyCode === 32) &&
				!selectors.menu?.contains(e.target) &&
				!selectors.menuList?.contains(e.target)
			) {
				addSentryBreadcrumb({
					category: "keydown",
					message: `Task ${id}`,
				});
				e.preventDefault();
				history.push(`/task/${id}`);
			}
		},
		[getMenuSelectors, history]
	);

	const handleDeleteActivity = async () => deleteActivity(deleteId);

	/**
	 * Load tasks on mount
	 */
	useEffect(() => {
		if (!init) {
			// Load activities from API
			api.workflow
				.getTasks({
					offset: 0,
					limit: 10,
				})
				.then(results => {
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setData(results);
						setTotal(results.totalCount);
						ready();
						setInit(true);
					}
				})
				.catch(e => console.error(e));
		}
	}, [_isMounted, init, ready, setData]);

	/**
	 * Search for tasks when search value has changed
	 */
	useEffect(() => {
		if (init) {
			searching();
			setCurrentPage(1);
			// Load activities from API with search query
			api.workflow
				.getTasks({ limit: 10, offset: 0, search: search })
				.then(results => {
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setData(results);
						setTotal(results.totalCount);
						ready();
					}
				})
				.catch(e => console.error(e));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search]);

	/**
	 * Page tasks when pagination button is clicked
	 */
	const pageData = useCallback(
		(page: number) => {
			addSentryBreadcrumb({
				category: "button.click",
				message: "Page my tasks",
			});

			loading();
			api.workflow
				.getTasks({
					limit,
					offset: (page - 1) * limit,
					search: state.search,
				})
				.then(res => {
					setData(res);
					ready();
				})
				.catch(e => console.error(e));
			setCurrentPage(page);
		},
		[setData, loading, ready, limit, state.search]
	);

	if (!init || state.searching)
		return <Loading className="mt-4 elem__loader--md" />;

	return (
		<>
			{state.results.length ? (
				<>
					<TasksHeading loading={state.loading} />
					<ListGroup className="tasks-group list-group-links">
						{state.results.map((task, index: number) => (
							<ListGroupItem
								className={`task-card task-card--claimed task-card--${
									task.taskRead ? "read" : "unread"
								} ${disableOnLoad}`}
								key={`myTask__${index}`}>
								{/* CustomLink checks click and keydown didn't come from the dropdown menu */}
								<CustomLink
									handleClick={(e: React.MouseEvent) =>
										handleOnClick(e, task.id)
									}
									handleKeyDown={(e: React.KeyboardEvent) =>
										handleKeyDown(e, task.id)
									}
									label={task.taskName}
									to={`/task/${task.id}`}>
									<MyTaskCard
										{...task}
										onReadUnreadClick={handleReadUnread}
										changePriority={changePriority}
										unassignTask={unassignTask}
										deleteActivity={deleteActivity}
									/>
								</CustomLink>
							</ListGroupItem>
						))}
					</ListGroup>
				</>
			) : (
				!state.loading && (
					<p className="no-tasks">
						{t(
							search.trim() !== ""
								? "main.tasks.text.noSearch"
								: "main.tasks.text.noMyTasks"
						)}
					</p>
				)
			)}

			{total !== undefined && total > limit && !state.searching && (
				<PaginationBar
					currentPage={currentPage}
					limit={limit}
					total={total}
					paginationLimit={paginationLimit}
					onPageChange={page => {
						pageData(page);
					}}
					loading={state.loading}
				/>
			)}

			{reassign && <ReassignDialog id={reassign} />}

			{deleteId && (
				<DeleteDialog
					isOpen
					deleteElement={handleDeleteActivity}
					title={t("main.tasks.labels.deleteActivity")}
					content={t("main.tasks.text.deleteActivity")}
				/>
			)}
		</>
	);
}

export default MyTasks;
